import { useRef, useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { UploadCloud, Upload, RefreshCw, Info, AlertCircle } from "lucide-react"
import { api } from "@/app/api/client"
import { useClientTheme } from "@/app/components/ThemeContext"
import { PageHeader } from "@/app/components/PageHeader"
import { useCurrentUser } from "@/app/hooks/useCurrentUser"
import type { ClientTheme } from "@/app/lib/themes"
import { ErrorBanner, ReadOnlyCard, formatApiError } from "@/app/components/FormPrimitives"
import { primaryButton } from "@/app/lib/buttonStyles"

interface ImportSummary {
  inserted: number
  updated: number
  unchanged: number
  skipped_app_touched: number
  errored: { tx_number: string; reason: string }[]
}

// Mineral rows have no TX # yet (numbers are allocated on insert), so errors
// are keyed by CSV line + STR instead of tx_number.
interface MineralImportSummary {
  inserted: number
  errored: { line: number; str: string; reason: string }[]
}

export function ImportPage() {
  const { theme } = useClientTheme()
  const { isStaff, isLoading: userLoading } = useCurrentUser()
  const queryClient = useQueryClient()

  const invalidate = () => {
    for (const key of ["inventories", "transactions", "orri_inventories"]) {
      queryClient.invalidateQueries({ queryKey: [key] })
    }
  }

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<ImportSummary>("/imports", body),
    onSuccess: invalidate,
  })
  const mineralMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<MineralImportSummary>("/imports", body),
    onSuccess: invalidate,
  })

  function importSheet() {
    mutation.reset()
    mutation.mutate({ source: "google_sheet" })
  }

  async function importFile(file: File | undefined | null) {
    if (!file) return
    mutation.reset()
    const text = await file.text()
    mutation.mutate({ source: "file", csv: text })
  }

  async function importMineralFile(file: File | undefined | null) {
    if (!file) return
    mineralMutation.reset()
    const text = await file.text()
    mineralMutation.mutate({ source: "file", kind: "mineral", csv: text })
  }

  if (userLoading) {
    return (
      <div className="p-4 sm:px-6 sm:pt-5 sm:pb-6">
        <div
          className="max-w-[900px]"
          style={{ color: theme.textMuted, fontFamily: "var(--font-ui)" }}
        >
          Loading…
        </div>
      </div>
    )
  }

  if (!isStaff) {
    return (
      <ReadOnlyCard
        theme={theme}
        title="Import"
        subtitle="Import transactions from the source sheet or a file"
        icon={<Upload size={17} strokeWidth={1.75} />}
      />
    )
  }

  const summary = mutation.data
  const mineralSummary = mineralMutation.data

  return (
    <div className="p-4 sm:px-6 sm:pt-5 sm:pb-6">
      <div className="max-w-[900px]">
        <PageHeader
          title="Import"
          subtitle="Import transactions from the source sheet or a file"
          theme={theme}
          icon={<Upload size={17} strokeWidth={1.75} />}
        />

        {/* ── Leasehold ─────────────────────────────────────────────────── */}
        <SectionLabel theme={theme}>Leasehold Import</SectionLabel>

        {/* The idempotent re-import behavior is leasehold-only (keyed on TX #);
            mineral rows have no key and always insert. */}
        <div
          className="rounded-lg p-3 flex items-start gap-2 mb-6"
          style={{
            backgroundColor: `${theme.brandPrimary}0C`,
            border: `1px solid ${theme.brandPrimary}28`,
          }}
        >
          <Info size={15} style={{ color: theme.brandPrimary, marginTop: "1px", flexShrink: 0 }} />
          <span
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: "12px",
              color: theme.textMuted,
              lineHeight: 1.45,
            }}
          >
            Re-importing is safe. Rows that have been edited in the app are skipped (reported as
            “skipped (app touched)”), so an import never clobbers local edits.
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6">
          {/* Google Sheet */}
          <div
            className="rounded-lg p-6 flex flex-col"
            style={{ backgroundColor: theme.bgSurface, border: `1px solid ${theme.borderColor}` }}
          >
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: "10px",
                fontWeight: 600,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: theme.textMuted,
                marginBottom: "6px",
              }}
            >
              From Google Sheet
            </div>
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: "12px",
                color: theme.textMuted,
                marginBottom: "16px",
                flex: 1,
              }}
            >
              Pull the latest transactions directly from the connected Google Sheet.
            </div>
            <button
              type="button"
              onClick={importSheet}
              disabled={mutation.isPending}
              className="btn"
              style={{
                ...primaryButton(theme),
                opacity: mutation.isPending ? 0.6 : 1,
                justifyContent: "center",
              }}
            >
              <RefreshCw size={14} />{" "}
              {mutation.isPending ? "Importing…" : "Import from Google Sheet"}
            </button>
          </div>

          {/* File */}
          <FileDropzone
            title="Import from File"
            label="Upload leasehold CSV"
            pending={mutation.isPending}
            onFile={importFile}
            theme={theme}
          />
        </div>

        {mutation.isError && <ErrorBanner theme={theme} message={formatApiError(mutation.error)} />}

        {summary && (
          <div
            className="rounded-lg p-6"
            style={{ backgroundColor: theme.bgSurface, border: `1px solid ${theme.borderColor}` }}
          >
            <PanelLabel theme={theme}>Import Summary</PanelLabel>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <SummaryStat
                label="Inserted"
                value={summary.inserted}
                color={theme.brandTeal}
                theme={theme}
              />
              <SummaryStat
                label="Updated"
                value={summary.updated}
                color={theme.brandPrimary}
                theme={theme}
              />
              <SummaryStat
                label="Unchanged"
                value={summary.unchanged}
                color={theme.textMuted}
                theme={theme}
              />
              <SummaryStat
                label="Skipped (app touched)"
                value={summary.skipped_app_touched}
                color={theme.brandGold}
                theme={theme}
              />
            </div>

            {summary.errored.length > 0 && (
              <ErroredTable
                theme={theme}
                count={summary.errored.length}
                headers={["TX #", "Reason"]}
                rows={summary.errored.map((row, i) => ({
                  key: `${row.tx_number}-${i}`,
                  cells: [row.tx_number || "—", row.reason],
                }))}
              />
            )}
          </div>
        )}

        {/* ── Mineral ───────────────────────────────────────────────────── */}
        <div className="mt-8">
          <SectionLabel theme={theme}>Mineral Import</SectionLabel>

          <div className="mb-6">
            <FileDropzone
              title="Import Minerals from File"
              label="Upload mineral CSV"
              pending={mineralMutation.isPending}
              onFile={importMineralFile}
              theme={theme}
              helper="Columns: S-T-R, Sec, T-R, County, Net Acres, Deal No., Source, Comments — optional: Date, Royalty, Interest Status, Legal Desc. Rows import as mineral acquisitions; missing dates get the import date. Re-uploading the same file creates duplicate rows."
            />
          </div>

          {mineralMutation.isError && (
            <ErrorBanner theme={theme} message={formatApiError(mineralMutation.error)} />
          )}

          {mineralSummary && (
            <div
              className="rounded-lg p-6"
              style={{ backgroundColor: theme.bgSurface, border: `1px solid ${theme.borderColor}` }}
            >
              <PanelLabel theme={theme}>Mineral Import Summary</PanelLabel>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <SummaryStat
                  label="Inserted"
                  value={mineralSummary.inserted}
                  color={theme.brandTeal}
                  theme={theme}
                />
              </div>

              {mineralSummary.errored.length > 0 && (
                <ErroredTable
                  theme={theme}
                  count={mineralSummary.errored.length}
                  headers={["Line", "STR", "Reason"]}
                  rows={mineralSummary.errored.map((row, i) => ({
                    key: `${row.line}-${i}`,
                    cells: [String(row.line), row.str || "—", row.reason],
                  }))}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// CSV dropzone card shared by the leasehold and mineral importers — same
// visuals; drag state and the hidden file input are fully internal (nothing
// outside reads them). Keyboard-accessible: the card is a focusable button
// role and Enter/Space open the file picker, like the click path.
function FileDropzone({
  title,
  label,
  pending,
  onFile,
  theme,
  helper,
}: {
  title: string
  /** Accessible name for the dropzone (e.g. "Upload leasehold CSV"). */
  label: string
  pending: boolean
  onFile: (file: File | undefined | null) => void
  theme: ClientTheme
  helper?: string
}) {
  const [dragOver, setDragOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={label}
      onDragOver={(e) => {
        e.preventDefault()
        setDragOver(true)
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragOver(false)
        onFile(e.dataTransfer.files?.[0])
      }}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          inputRef.current?.click()
        }
      }}
      className="rounded-lg p-6 flex flex-col items-center justify-center text-center"
      style={{
        backgroundColor: dragOver ? `${theme.brandPrimary}0C` : theme.bgSurface,
        border: `2px dashed ${dragOver ? theme.brandPrimary : theme.borderColor}`,
        cursor: "pointer",
        transition: "all 150ms ease",
      }}
    >
      <UploadCloud size={28} style={{ color: theme.brandPrimary, marginBottom: "8px" }} />
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: "13px",
          fontWeight: 600,
          color: theme.textPrimary,
        }}
      >
        {pending ? "Importing…" : title}
      </div>
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: "12px",
          color: theme.textMuted,
          marginTop: "4px",
        }}
      >
        Drop a CSV here, or click to browse.
      </div>
      {helper && (
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: "11px",
            color: theme.textDim,
            marginTop: "8px",
            lineHeight: 1.5,
            maxWidth: "560px",
          }}
        >
          {helper}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        style={{ display: "none" }}
        onChange={(e) => {
          onFile(e.target.files?.[0])
          e.target.value = ""
        }}
      />
    </div>
  )
}

// Errored-rows table shared by both summaries (they differ only in columns).
function ErroredTable({
  theme,
  count,
  headers,
  rows,
}: {
  theme: ClientTheme
  count: number
  headers: string[]
  rows: { key: string; cells: string[] }[]
}) {
  return (
    <div className="mt-6">
      <div className="flex items-center gap-1.5 mb-2">
        <AlertCircle size={14} style={{ color: theme.brandAccent }} />
        <span
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: "12px",
            fontWeight: 600,
            color: theme.brandAccent,
          }}
        >
          {count} row{count === 1 ? "" : "s"} errored
        </span>
      </div>
      <div
        className="rounded-lg overflow-hidden"
        style={{ border: `1px solid ${theme.borderColor}` }}
      >
        <table className="w-full border-collapse">
          <thead>
            <tr
              style={{
                backgroundColor: theme.bgSurface2,
                borderBottom: `1px solid ${theme.borderColor}`,
              }}
            >
              {headers.map((h) => (
                <th key={h} className="px-4 py-2 text-left" style={headStyle(theme)}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} style={{ borderBottom: `1px solid ${theme.borderColor}` }}>
                {row.cells.map((cell, i) => (
                  <td
                    key={i}
                    className="px-4 py-2"
                    style={
                      // Last column is the human-readable reason; earlier
                      // columns are identifiers (TX # / line / STR).
                      i === row.cells.length - 1
                        ? {
                            fontFamily: "var(--font-ui)",
                            fontSize: "12px",
                            color: theme.textMuted,
                          }
                        : {
                            fontFamily: "var(--font-data)",
                            fontSize: "12px",
                            color: theme.textPrimary,
                          }
                    }
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// Real headings (h2/h3, styled identically to the old divs) so screen-reader
// users can jump between the import sections and their summary panels.
function SectionLabel({ children, theme }: { children: React.ReactNode; theme: ClientTheme }) {
  return (
    <h2
      style={{
        fontFamily: "var(--font-heading)",
        fontSize: "14px",
        fontWeight: 600,
        color: theme.textPrimary,
        marginBottom: "10px",
      }}
    >
      {children}
    </h2>
  )
}

function PanelLabel({ children, theme }: { children: React.ReactNode; theme: ClientTheme }) {
  return (
    <h3
      style={{
        fontFamily: "var(--font-ui)",
        fontSize: "10px",
        fontWeight: 600,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: theme.textMuted,
        marginBottom: "16px",
      }}
    >
      {children}
    </h3>
  )
}

function SummaryStat({
  label,
  value,
  color,
  theme,
}: {
  label: string
  value: number
  color: string
  theme: ClientTheme
}) {
  return (
    <div
      className="rounded-lg p-4"
      style={{ backgroundColor: theme.bgSurface2, border: `1px solid ${theme.borderColor}` }}
    >
      <div style={{ fontFamily: "var(--font-data)", fontSize: "28px", fontWeight: 600, color }}>
        {value}
      </div>
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: "11px",
          fontWeight: 600,
          color: theme.textDim,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          marginTop: "2px",
        }}
      >
        {label}
      </div>
    </div>
  )
}

function headStyle(theme: ClientTheme): React.CSSProperties {
  return {
    fontFamily: "var(--font-ui)",
    fontSize: "10px",
    fontWeight: 600,
    color: theme.textDim,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  }
}
